import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { EntityListResponse } from "@/jenesys-types/data/crud";
import { AppError } from "@/jenesys-types/errors";
import { Notification } from "@/jenesys-types/jenesys-entities/config";

import {
  selectActiveClientId,
  selectActiveCompanyId
} from "@/store/features/companies/companies";

export interface ClientNotificationsState {
  pendingAlert: boolean;
  status: "NOT_LOADED" | "LOADING" | "LOADED" | "FAILED";
  olderNotifications: Record<string, Notification>;
  streamedNotifications: Record<string, Notification>;
}

export interface NotificationsState {
  [companyId: string]: ClientNotificationsState;
}

export const defaultClientNotificationsState: ClientNotificationsState = {
  pendingAlert: false,
  status: "NOT_LOADED",
  olderNotifications: {},
  streamedNotifications: {}
};

export const initialState: NotificationsState = {};

export type GetNotificationsAction = PayloadAction<{ companyId: string }>;

export type GetNotificationsSuccessAction = PayloadAction<{
  companyId: string;
  response: EntityListResponse<Notification>;
}>;

interface GetNotificationsFailurePayload {
  companyId: string;
  error?: AppError;
}
export type GetNotificationsFailureAction =
  PayloadAction<GetNotificationsFailurePayload>;

export type StreamNotificationAction = PayloadAction<{
  companyId: string;
  notification: Notification;
}>;

export type DeleteNotificationAction = PayloadAction<{
  companyId: string;
  id: string;
}>;

export type DeleteAllNotificationsAction = PayloadAction<{ companyId: string }>;

export type DeleteNotificationFailureAction = PayloadAction<{
  companyId: string;
  id: string;
  error?: AppError;
}>;

export type DeleteAllNotificationsFailureAction = PayloadAction<{
  companyId: string;
  error?: AppError;
}>;

export type MarkNotificationAsReadAction = PayloadAction<{
  companyId: string;
  id: string;
}>;

export type MarkNotificationAsReadFailureAction = PayloadAction<{
  companyId: string;
  id: string;
  error?: AppError;
}>;

export type MarkAllNotificationsAsReadAction = PayloadAction<{
  companyId: string;
}>;

export type MarkAllNotificationsAsReadFailureAction = PayloadAction<{
  companyId: string;
  error?: AppError;
}>;

export type DismissAlertAction = PayloadAction<{ companyId: string }>;

const name = "app/notifications";

// Cant use spread operator here because defaultClientNotificationState gets frozen
// by immer when building the slice, spread makes a shallow copy but nested objects
// are still referenced to the frozen object.

const appSlice = createSlice({
  name,
  initialState,
  reducers: {
    getNotifications: {
      reducer: (state: NotificationsState, action: GetNotificationsAction) => {
        const { companyId } = action.payload;
        if (!state[companyId]) {
          state[companyId] = {
            pendingAlert: false,
            status: "NOT_LOADED",
            olderNotifications: {},
            streamedNotifications: {}
          };
        }
        state[companyId].status = "LOADING";
      },
      prepare: (companyId: string) => ({ payload: { companyId } })
    },
    getNotificationsFailure: {
      reducer: (
        state: NotificationsState,
        action: GetNotificationsFailureAction
      ) => {
        const { companyId } = action.payload;
        if (!state[companyId]) {
          state[companyId] = {
            pendingAlert: false,
            status: "NOT_LOADED",
            olderNotifications: {},
            streamedNotifications: {}
          };
        }
        state[companyId].status = "FAILED";
      },
      prepare: (companyId: string, error?: AppError) => ({
        payload: { companyId, error }
      })
    },
    getNotificationsSuccess: {
      reducer: (
        state: NotificationsState,
        action: GetNotificationsSuccessAction
      ) => {
        const { companyId, response } = action.payload;
        if (!state[companyId]) {
          state[companyId] = {
            pendingAlert: false,
            status: "NOT_LOADED",
            olderNotifications: {},
            streamedNotifications: {}
          };
        }
        state[companyId].status = "LOADED";
        state[companyId].olderNotifications = Object.fromEntries(
          response.items.map((notification) => [notification.id, notification])
        );
        if (response.items.length > 0) {
          state[companyId].pendingAlert = true;
        }
      },
      prepare: (
        companyId: string,
        response: EntityListResponse<Notification>
      ) => ({
        payload: { companyId, response }
      })
    },
    streamNotification: {
      reducer: (
        state: NotificationsState,
        action: StreamNotificationAction
      ) => {
        const { companyId, notification } = action.payload;
        if (!state[companyId]) {
          state[companyId] = {
            pendingAlert: false,
            status: "NOT_LOADED",
            olderNotifications: {},
            streamedNotifications: {}
          };
        }
        state[companyId].streamedNotifications[notification.id] = notification;
        state[companyId].pendingAlert = true;
      },
      prepare: (companyId: string, notification: Notification) => ({
        payload: { companyId, notification }
      })
    },
    deleteNotification: {
      reducer: (
        state: NotificationsState,
        action: DeleteNotificationAction
      ) => {
        const { companyId, id } = action.payload;
        if (!state[companyId]) {
          state[companyId] = {
            pendingAlert: false,
            status: "NOT_LOADED",
            olderNotifications: {},
            streamedNotifications: {}
          };
        }
        delete state[companyId].olderNotifications[id];
        delete state[companyId].streamedNotifications[id];
      },
      prepare: (companyId: string, id: string) => ({
        payload: { companyId, id }
      })
    },
    deleteAllNotifications: {
      reducer: (
        state: NotificationsState,
        action: DeleteAllNotificationsAction
      ) => {
        const { companyId } = action.payload;
        if (!state[companyId]) {
          state[companyId] = {
            pendingAlert: false,
            status: "NOT_LOADED",
            olderNotifications: {},
            streamedNotifications: {}
          };
        }
        state[companyId].olderNotifications = {};
        state[companyId].streamedNotifications = {};
      },
      prepare: (companyId: string) => ({ payload: { companyId } })
    },
    deleteNotificationFailure: {
      reducer: (
        state: NotificationsState,
        action: DeleteNotificationFailureAction
      ) => {
        const { companyId } = action.payload;
        if (!state[companyId]) {
          state[companyId] = {
            pendingAlert: false,
            status: "NOT_LOADED",
            olderNotifications: {},
            streamedNotifications: {}
          };
        }
        state[companyId].status = "FAILED";
      },
      prepare: (companyId: string, id: string, error?: AppError) => ({
        payload: { companyId, id, error }
      })
    },
    deleteAllNotificationsFailure: {
      reducer: (
        state: NotificationsState,
        action: DeleteAllNotificationsFailureAction
      ) => {
        const { companyId } = action.payload;
        if (!state[companyId]) {
          state[companyId] = {
            pendingAlert: false,
            status: "NOT_LOADED",
            olderNotifications: {},
            streamedNotifications: {}
          };
        }
        state[companyId].status = "FAILED";
      },
      prepare: (companyId: string, error?: AppError) => ({
        payload: { companyId, error }
      })
    },
    markNotificationAsRead: {
      reducer: (
        state: NotificationsState,
        action: MarkNotificationAsReadAction
      ) => {
        const { companyId, id } = action.payload;
        if (!state[companyId]) {
          state[companyId] = {
            pendingAlert: false,
            status: "NOT_LOADED",
            olderNotifications: {},
            streamedNotifications: {}
          };
        }
        if (state[companyId].olderNotifications[id]) {
          state[companyId].olderNotifications[id].is_read = true;
        }
        if (state[companyId].streamedNotifications[id]) {
          state[companyId].streamedNotifications[id].is_read = true;
        }
      },
      prepare: (companyId: string, id: string) => ({
        payload: { companyId, id }
      })
    },
    markAllNotificationsAsRead: {
      reducer: (
        state: NotificationsState,
        action: MarkAllNotificationsAsReadAction
      ) => {
        const { companyId } = action.payload;
        if (!state[companyId]) {
          state[companyId] = {
            pendingAlert: false,
            status: "NOT_LOADED",
            olderNotifications: {},
            streamedNotifications: {}
          };
        }
        Object.values(state[companyId].olderNotifications).forEach(
          (notification) => {
            notification.is_read = true;
          }
        );
        Object.values(state[companyId].streamedNotifications).forEach(
          (notification) => {
            notification.is_read = true;
          }
        );
      },
      prepare: (companyId: string) => ({ payload: { companyId } })
    },
    markNotificationAsReadFailure: {
      reducer: (
        state: NotificationsState,
        action: MarkNotificationAsReadFailureAction
      ) => {
        const { companyId, id } = action.payload;
        if (!state[companyId]) {
          state[companyId] = {
            pendingAlert: false,
            status: "NOT_LOADED",
            olderNotifications: {},
            streamedNotifications: {}
          };
        }
        // Revert the is_read status back to false
        if (state[companyId].olderNotifications[id]) {
          state[companyId].olderNotifications[id].is_read = false;
        }
        if (state[companyId].streamedNotifications[id]) {
          state[companyId].streamedNotifications[id].is_read = false;
        }
        state[companyId].status = "FAILED";
      },
      prepare: (companyId: string, id: string, error?: AppError) => ({
        payload: { companyId, id, error }
      })
    },
    markAllNotificationsAsReadFailure: {
      reducer: (
        state: NotificationsState,
        action: MarkAllNotificationsAsReadFailureAction
      ) => {
        const { companyId } = action.payload;
        if (!state[companyId]) {
          state[companyId] = {
            pendingAlert: false,
            status: "NOT_LOADED",
            olderNotifications: {},
            streamedNotifications: {}
          };
        }
        // Revert all notifications' is_read status back to false
        Object.values(state[companyId].olderNotifications).forEach(
          (notification) => {
            notification.is_read = false;
          }
        );
        Object.values(state[companyId].streamedNotifications).forEach(
          (notification) => {
            notification.is_read = false;
          }
        );
        state[companyId].status = "FAILED";
      },
      prepare: (companyId: string, error?: AppError) => ({
        payload: { companyId, error }
      })
    },
    dismissAlert: {
      reducer: (state: NotificationsState, action: DismissAlertAction) => {
        const { companyId } = action.payload;
        if (!state[companyId]) {
          state[companyId] = {
            pendingAlert: false,
            status: "NOT_LOADED",
            olderNotifications: {},
            streamedNotifications: {}
          };
        }
        state[companyId].pendingAlert = false;
      },
      prepare: (companyId: string) => ({ payload: { companyId } })
    }
  }
});

export const {
  getNotifications,
  getNotificationsFailure,
  getNotificationsSuccess,
  streamNotification,
  deleteNotification,
  deleteNotificationFailure,
  deleteAllNotifications,
  deleteAllNotificationsFailure,
  markNotificationAsRead,
  markNotificationAsReadFailure,
  markAllNotificationsAsRead,
  markAllNotificationsAsReadFailure,
  dismissAlert
} = appSlice.actions;

const selectNotificationsState = (state: {
  app: { notifications: NotificationsState };
}) => state.app.notifications;

export const selectNotificationsPendingAlert = createSelector(
  [selectNotificationsState, selectActiveClientId, selectActiveCompanyId],
  (notifications, clientId, companyId) => {
    const scopeId = clientId || companyId;
    return (
      (scopeId && notifications[scopeId]) ||
      defaultClientNotificationsState
    ).pendingAlert;
  }
);

export const selectNotificationsLoadingStatus = createSelector(
  [selectNotificationsState, selectActiveClientId, selectActiveCompanyId],
  (notifications, clientId, companyId) => {
    const scopeId = clientId || companyId;
    return (
      (scopeId && notifications[scopeId]) ||
      defaultClientNotificationsState
    ).status;
  }
);

export const selectNotifications = createSelector(
  [selectNotificationsState, selectActiveClientId, selectActiveCompanyId],
  (notifications, clientId, companyId) => {
    const scopeId = clientId || companyId;
    const state =
      (scopeId && notifications[scopeId]) || defaultClientNotificationsState;
    const allNotifications = new Map([
      ...Object.entries(state.olderNotifications),
      ...Object.entries(state.streamedNotifications)
    ]);
    return [...allNotifications.values()].sort((a, b) =>
      b.date.localeCompare(a.date)
    );
  }
);

export default appSlice.reducer;
